import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogBaseExtendProps } from 'components/_new/Dialog';
import { sessionsApi } from 'api/sessions';
import { SessionTypeEnum } from 'types/Session';
import { usePair } from 'contexts/pairContext';
import { Dropdown } from 'components/_new/Dropdown';
import { usePanel } from 'contexts/panelContext';
import { Field } from 'components/_new/Field';
import { MessageBox } from 'components/_new/MessageBox';
import { SessionFile } from 'types/SessionFile';
import { UploadUserFiles } from './UploadUserFiles';

type NewUploadUserFilesDialogProps = DialogBaseExtendProps & {
	onFinish: () => void;
	onSave?: () => void;
	userFile?: SessionFile;
};

export const NewUploadUserFilesDialog = ({
	onFinish,
	onSave,
	userFile,
	...dialogProps
}: NewUploadUserFilesDialogProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { currentProgramMembership, panelLinkPrefix } = usePanel();
	const { pair } = usePair();
	const pairId = Number(pair.id);

	const isEditMode = Boolean(userFile);

	const { data: sessionsData, isLoading } = useQuery(
		['sessions', { pairId }],
		() => sessionsApi.getSessions(pairId, currentProgramMembership.id),
		{
			enabled: dialogProps.visible && !isEditMode,
		}
	);

	const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
	const [hasChanges, setHasChanges] = useState(false);

	const session = sessionsData?.find(({ id }) => id === selectedSessionId);

	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (hasChanges && dialogProps.visible) {
				e.preventDefault();
				e.returnValue = '';
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [hasChanges, dialogProps.visible]);

	useEffect(() => {
		if (!dialogProps.visible) {
			setSelectedSessionId(null);
			setHasChanges(false);
		}
		return () => {
			setSelectedSessionId(null);
			setHasChanges(false);
		};
	}, [dialogProps.visible]);

	const handleClose = () => {
		if (hasChanges) {
			if (window.confirm(t('misc.confirmReject'))) {
				setHasChanges(false);
				dialogProps.onHide?.();
			}
			return;
		}

		dialogProps.onHide?.();
	};

	return (
		<Dialog
			title={t(isEditMode ? 'userPanel.userFiles.modal.edit' : 'userPanel.userFiles.modal.add')}
			{...dialogProps}
			onHide={handleClose}
			dialogSize="lg"
			bodyClassName="flex flex-column gap-0"
		>
			{!isEditMode && (
				<>
					<MessageBox
						title={t('userPanel.userFiles.file.forMeAndPair')}
						variant="rose"
						icon="eye"
						iconSet="fa"
						className="mb-3"
					/>
					<Field htmlFor="session" label={t('userPanel.userFiles.file.session')}>
						<Dropdown
							inputId="session"
							options={(sessionsData || []).map(({ id, sessionNumber, sessionType }) => ({
								value: id,
								label: `${t('userPanel.sessions.sessionNo', { no: sessionNumber })}${
									sessionType && sessionType !== SessionTypeEnum.STANDARD
										? ` (${t(`userPanel.sessions.${sessionType}.title`)})`
										: ''
								}`,
							}))}
							disabled={isLoading}
							value={selectedSessionId}
							onChange={({ target: { value } }) => {
								setSelectedSessionId(value);
								setHasChanges(true);
							}}
						/>
					</Field>
				</>
			)}

			{(isEditMode || session) && (
				<UploadUserFiles
					userFile={userFile}
					setUserFile={() => {}}
					setHasChanges={setHasChanges}
					onFinish={(sessionFile) => {
						if (isEditMode) {
							onFinish();
							dialogProps.onHide?.();
						} else {
							navigate(`/${panelLinkPrefix}/pair/${pairId}/userfiles/display/${sessionFile.id}`);
						}
					}}
					onSave={() => {
						onSave?.();
						setHasChanges(false);
					}}
					session={session}
					withAddNewButton
				/>
			)}
		</Dialog>
	);
};
